/**
 * MyFeedback — user-side overzicht van eigen feedback + status timeline
 *
 * Sprint follow-up (5 Feb 2026): elke gebruiker kan zijn eigen submitted
 * feedback zien met status:
 *   new · in_progress · retesting · resolved
 *
 * Voor items in `retesting` state kan de user zelf bevestigen:
 *   ✅ Fix is OK      → status wordt `resolved`
 *   ❌ Nog niet OK   → status gaat terug naar `in_progress` + `reopened_by_user`
 *
 * Route: /app/feedback
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { PageHeader } from "./Dashboard";
import {
    MessageSquare, Loader2, Check, X, RefreshCw, ChevronDown, ChevronRight,
    Clock, Sparkles, AlertCircle, ThumbsUp, MessageCircle,
} from "lucide-react";

const STATUS_META = {
    new:         { get label() { return i18n.t("myfb.st_new"); },         color: "#94a3b8", icon: Clock,       get description() { return i18n.t("myfb.st_new_d"); } },
    in_progress: { get label() { return i18n.t("myfb.st_prog"); }, color: "#eab308", icon: Sparkles,    get description() { return i18n.t("myfb.st_prog_d"); } },
    retesting:   { get label() { return i18n.t("myfb.st_retest"); },  color: "#a855f7", icon: RefreshCw,   get description() { return i18n.t("myfb.st_retest_d"); } },
    resolved:    { get label() { return i18n.t("myfb.st_done"); },      color: "#22c55e", icon: ThumbsUp,     get description() { return i18n.t("myfb.st_done_d"); } },
};

const TYPE_LABEL = {
    bug: "Bug",
    get suggestion() { return i18n.t("myfb.type_idea"); },
    get question() { return i18n.t("myfb.type_question"); },
    compliment: "Compliment",
};

export default function MyFeedback() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [confirming, setConfirming] = useState(null);   // id of item currently being confirmed
    const [rejectNote, setRejectNote] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/feedback/mine").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const counts = items.reduce((acc, i) => {
        acc[i.status] = (acc[i.status] || 0) + 1; return acc;
    }, {});

    const confirmVerified = async (id) => {
        setConfirming(id);
        try {
            await api.post(`/feedback/${id}/user-confirm`, { verified: true });
            toast.success("Bedankt! Feedback op 'opgelost' gezet.");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("myfb.confirm_failed"));
        } finally { setConfirming(null); }
    };

    const rejectFix = async (id) => {
        if (!rejectNote.trim()) { toast.error(i18n.t("myfb.note_required")); return; }
        setConfirming(id);
        try {
            await api.post(`/feedback/${id}/user-confirm`, { verified: false, note: rejectNote });
            toast.info(i18n.t("myfb.thanks_recheck"));
            setRejectNote("");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kon niet afwijzen.");
        } finally { setConfirming(null); }
    };

    return (
        <PageHeader
            title={i18n.t("myfb.title")}
            subtitle={i18n.t("myfb.subtitle")}
        >
            {/* Status counters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {Object.entries(STATUS_META).map(([key, meta]) => {
                    const Icon = meta.icon;
                    const n = counts[key] || 0;
                    return (
                        <div key={key}
                            data-testid={`my-fb-counter-${key}`}
                            style={{ borderLeft: `3px solid ${meta.color}` }}
                            className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{meta.label}</span>
                                <Icon size={14} style={{ color: meta.color }}/>
                            </div>
                            <div className="mt-2 font-display text-3xl tracking-tighter">{n}</div>
                            <div className="text-[11px] text-[var(--jr-text-2)] mt-1">{meta.description}</div>
                        </div>
                    );
                })}
            </div>

            {/* Retesting banner (call-to-action) */}
            {(counts.retesting || 0) > 0 && (
                <div className="mb-4 rounded-lg border border-[#a855f7]/40 bg-[#a855f7]/10 p-3 flex items-center gap-3" data-testid="retesting-banner">
                    <RefreshCw size={16} className="text-[#a855f7]"/>
                    <div className="flex-1 text-sm text-white">
                        <b>{counts.retesting}</b> item{counts.retesting !== 1 ? "s" : ""} wacht{counts.retesting === 1 ? "" : "en"} op jouw bevestiging.
                    </div>
                </div>
            )}

            {/* Items list */}
            {loading ? (
                <div className="text-center py-10 text-sm text-[var(--jr-text-3)]"><Loader2 size={16} className="animate-spin inline mr-2"/>{i18n.t("common.loading")}</div>
            ) : items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/10 p-10 text-center">
                    <MessageSquare size={24} className="mx-auto text-[var(--jr-text-3)] mb-2"/>
                    <div className="text-sm text-[var(--jr-text-2)]">{i18n.t("myfb.none_yet")}</div>
                    <div className="text-xs text-[var(--jr-text-3)] mt-1">{i18n.t("myfb.use_button")}</div>
                </div>
            ) : (
                <div className="space-y-2" data-testid="my-fb-list">
                    {items.map((it) => {
                        const meta = STATUS_META[it.status] || STATUS_META.new;
                        const Icon = meta.icon;
                        const expanded = expandedId === it.id;
                        const needsAction = it.status === "retesting" && it.user_confirmed !== true;
                        return (
                            <div key={it.id}
                                data-testid={`my-fb-${it.id}`}
                                className={`rounded-xl border p-4 transition-colors ${needsAction ? "border-[#a855f7]/40 bg-[#a855f7]/[0.04]" : "border-white/8 bg-[var(--jr-surface)]"}`}>
                                <button className="w-full text-left" onClick={() => setExpandedId(expanded ? null : it.id)}>
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 shrink-0">
                                            <Icon size={14} style={{ color: meta.color }}/>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
                                                <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{TYPE_LABEL[it.type] || it.type}</span>
                                                <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{it.priority?.toUpperCase()}</span>
                                                {it.reopened_by_user && <span className="text-[9px] font-mono uppercase text-[#ef4444] bg-[#ef4444]/15 px-1.5 py-0.5 rounded">{i18n.t("myfb.reopened")}</span>}
                                            </div>
                                            <div className="mt-1 text-sm text-white font-medium truncate">{it.title}</div>
                                            <div className="text-xs text-[var(--jr-text-2)] mt-0.5 line-clamp-1">{it.message}</div>
                                            <div className="text-[10px] text-[var(--jr-text-3)] mt-1">
                                                {new Date(it.created_at).toLocaleDateString("nl-BE", { day: "2-digit", month: "short", year: "numeric" })}
                                                {it.assignee_name && <span> · {i18n.t("myfb.assigned_to")} {it.assignee_name}</span>}
                                                {(it.replies || []).length > 0 && <span> · <MessageCircle size={9} className="inline"/> {it.replies.length}</span>}
                                            </div>
                                        </div>
                                        {expanded ? <ChevronDown size={14} className="text-[var(--jr-text-3)]"/> : <ChevronRight size={14} className="text-[var(--jr-text-3)]"/>}
                                    </div>
                                </button>

                                {expanded && (
                                    <div className="mt-3 pt-3 border-t border-white/8 space-y-3">
                                        <p className="text-xs text-[var(--jr-text-2)] whitespace-pre-wrap">{it.message}</p>

                                        {(it.replies || []).length > 0 && (
                                            <div className="space-y-2">
                                                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">Thread</div>
                                                {it.replies.map((r) => (
                                                    <div key={r.id} className={`rounded-lg p-2 text-xs ${r.author_role === "admin" ? "bg-[var(--jr-brand)]/10 border border-[var(--jr-brand)]/25" : "bg-white/5"}`}>
                                                        <div className="text-[10px] text-[var(--jr-text-3)] font-mono mb-1">
                                                            {r.author_name} · {new Date(r.created_at).toLocaleString("nl-BE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                                        </div>
                                                        <div className="text-[var(--jr-text-2)] whitespace-pre-wrap">{r.message}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Retest confirmation controls */}
                                        {needsAction && (
                                            <div className="rounded-lg border border-[#a855f7]/40 bg-[#a855f7]/[0.06] p-3">
                                                <div className="text-xs text-white font-medium mb-2 flex items-center gap-2">
                                                    <RefreshCw size={12} className="text-[#a855f7]"/>
                                                    {i18n.t("myfb.does_fix_work")}
                                                </div>
                                                <div className="text-[11px] text-[var(--jr-text-2)] mb-3">
                                                    {i18n.t("myfb.retest_explain")}
                                                </div>
                                                <div className="flex flex-col sm:flex-row gap-2">
                                                    <button onClick={() => confirmVerified(it.id)}
                                                        disabled={confirming === it.id}
                                                        data-testid={`fb-verify-ok-${it.id}`}
                                                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#22c55e] text-black text-xs font-medium hover:bg-[#22c55e]/85 disabled:opacity-50">
                                                        {confirming === it.id ? <Loader2 size={11} className="animate-spin"/> : <Check size={12}/>}
                                                        {i18n.t("myfb.yes_confirm")}
                                                    </button>
                                                </div>
                                                <div className="mt-3">
                                                    <textarea value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
                                                        placeholder={i18n.t("myfb.reject_ph")}
                                                        data-testid={`fb-reject-note-${it.id}`}
                                                        rows={2}
                                                        className="w-full text-xs bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-[var(--jr-text-3)] outline-none focus:border-[#ef4444]"/>
                                                    <button onClick={() => rejectFix(it.id)}
                                                        disabled={confirming === it.id || !rejectNote.trim()}
                                                        data-testid={`fb-verify-nok-${it.id}`}
                                                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium border border-[#ef4444]/40 text-[#ef4444] hover:bg-[#ef4444]/10 disabled:opacity-40">
                                                        <X size={11}/> Nog niet opgelost — heropenen
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {it.user_confirmed === true && it.status === "resolved" && (
                                            <div className="rounded-lg border border-[#22c55e]/30 bg-[#22c55e]/[0.06] p-2 text-xs text-[#22c55e] inline-flex items-center gap-2">
                                                <ThumbsUp size={12}/> Jij hebt bevestigd dat de fix werkt.
                                            </div>
                                        )}
                                        {it.reopened_by_user && (
                                            <div className="rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/[0.06] p-2 text-xs text-[#ef4444] inline-flex items-center gap-2">
                                                <AlertCircle size={12}/> Jij hebt aangegeven dat het nog niet opgelost is — heropend.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </PageHeader>
    );
}
